import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect} from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {Header3} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import runModal from 'ui-library/runModal';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import * as BenchlingAppActions from '../BenchlingAppActions';
import {
  BENCHLING_APPS_WEB_URL,
  deleteClientSecret,
  regenerateClientSecret,
  UpdateBenchlingAppDefinitionArgs,
  UpdateBenchlingAppDefinitionVersionArgs,
} from '../BenchlingAppDAO';
import {ClientCredentialSectionCredential} from './__generated/ClientCredentialSectionCredential';
import {
  getBenchlingAppQueryLegacy,
  getBenchlingAppQueryLegacyVariables,
} from './__generated/getBenchlingAppQueryLegacy';
import ClientCredentialSection, {
  CLIENT_CREDENTIAL_SECTION_CREDENTIAL_FRAGMENT,
} from './ClientCredentialSectionLegacy';
import DisplayClientSecretModal from './DisplayClientSecretModal';
import PublicKeySection from './PublicKeySectionLegacy';
import WebhookConfigurationSection, {
  WebhookConfigurationSectionWebhookConfiguration,
  WEBHOOK_CONFIGURATION_SECTION_WEBHOOK_CONFIGURATION,
} from './WebhookConfigurationSectionLegacy';
import WebhookSetupSection from './WebhookSetupSection';

type BenchlingApp = ExtractByTypename<getBenchlingAppQueryLegacy['benchlingApp'], 'BenchlingAppNode'>;

type OAuthCredential = ExtractByTypename<
  ClientCredentialSectionCredential['oauthCredential'],
  'OAuthCredentialNode'
>;

const BENCHLING_APP_SETTINGS_TAB = 'BENCHLING_APP_SETTINGS_TAB';

export const BENCHLING_APP_DETAILS_QUERY = gql`
  query getBenchlingAppQueryLegacy($benchlingAppId: StrictString!, $forDeveloper: StrictBoolean)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingApp(id: $benchlingAppId, forDeveloper: $forDeveloper) {
      ... on BenchlingAppNode {
        ...BenchlingAppGraphQL
        ...ClientCredentialSectionCredential
        ...WebhookConfigurationSectionWebhookConfiguration
        publicKey
        webhookUrl
        webhookHeaders {
          key
          value
        }
        isPrivate
        eventSubscription {
          id
          eventTypes
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BenchlingApp').fragment}
  ${CLIENT_CREDENTIAL_SECTION_CREDENTIAL_FRAGMENT}
  ${WEBHOOK_CONFIGURATION_SECTION_WEBHOOK_CONFIGURATION}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface BenchlingAppProps {
  id: string;
}

function currentTenantIsOwningTenant(benchlingApp: BenchlingApp): boolean {
  return benchlingApp.owningTenant.isCurrentTenant;
}

// TODO (BNCH-92479): remove this component when we migrate all legacy apps
export default function BenchlingAppPageLegacy(props: BenchlingAppProps): JSX.Element {
  const benchlingAppId = props.id;
  return (
    <BenchlingQuery<getBenchlingAppQueryLegacy, getBenchlingAppQueryLegacyVariables>
      query={BENCHLING_APP_DETAILS_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{benchlingAppId, forDeveloper: true}}
    >
      {(data, {refetch}) => {
        if (!isInaccessible(data.benchlingApp) && currentTenantIsOwningTenant(data.benchlingApp)) {
          return <BenchlingAppSettings benchlingApp={data.benchlingApp} refetchBenchlingApp={refetch} />;
        } else {
          return (
            <div>
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text="You do not have access to manage this app."
              />
            </div>
          );
        }
      }}
    </BenchlingQuery>
  );
}

export function BenchlingAppSettings({
  benchlingApp,
  refetchBenchlingApp,
}: {
  benchlingApp: BenchlingApp;
  refetchBenchlingApp: () => void;
}): JSX.Element {
  const {id: benchlingAppId, name, publicKey} = benchlingApp;
  const description = benchlingApp.description ?? '';
  const currentUpdateShape = {name, description};

  useEffect(() => {
    BenchlingUtil.setWindowTitle(`${name} - Benchling Apps`);
  }, [name]);

  async function handleSubmitDefinition(
    newValue: UpdateBenchlingAppDefinitionArgs,
    notifySuccess: boolean = true
  ): Promise<void> {
    const benchlingApp = await BenchlingAppActions.deprecatedUpdateDefinition(benchlingAppId, newValue);
    refetchBenchlingApp();
    if (notifySuccess) {
      notifications.success(
        <>
          Successfully updated app <em>{benchlingApp.name}</em>
        </>
      );
    }
  }

  async function handleSubmitDefinitionVersion(
    newValue: UpdateBenchlingAppDefinitionVersionArgs
  ): Promise<void> {
    const app = await BenchlingAppActions.deprecatedUpdateDefinitionVersion(
      benchlingAppId,
      benchlingApp.versionNumber,
      newValue
    );
    refetchBenchlingApp();
    notifications.success(
      <>
        Successfully updated app <em>{app.name}</em>
      </>
    );
  }

  async function handleRegenerateClientSecret(): Promise<void> {
    const rv = await regenerateClientSecret(benchlingApp.id);
    runModal(DisplayClientSecretModal, {
      clientSecret: rv.clientSecret,
      onRequestClose: () => {
        refetchBenchlingApp();
        notifications.success('Successfully generated a new Client Secret');
      },
      onRequestSubmit: () => {
        refetchBenchlingApp();
        notifications.success('Successfully generated a new Client Secret');
      },
    });
  }
  async function handleDeleteClientSecret(): Promise<void> {
    await deleteClientSecret(benchlingApp.id);
    refetchBenchlingApp();
    notifications.success('Successfully deleted Client Secret');
  }

  function getVersionLockedTooltip(): string | null {
    if (benchlingApp.isPrivate === true || benchlingApp.isPrivate == null) {
      return null;
    } else {
      return 'This field can not be edited because the current app version is no longer private';
    }
  }

  return (
    <SettingsPageContainer
      onClickTitle={() => {
        navigateToURL(BENCHLING_APPS_WEB_URL);
      }}
      subtitle={name}
      title="Apps"
      toolbar={
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.SMALL}
          text="View app in the workspace"
          onClick={() => {
            navigateToURL(benchlingApp.url);
          }}
        />
      }
    >
      <TabContainer
        activeTab={BENCHLING_APP_SETTINGS_TAB}
        className={classNames(css(styles.maxWidth), 'u-flex-grow')}
        onRequestTabChange={() => {}}
      >
        <Tab tabKey={BENCHLING_APP_SETTINGS_TAB} title="Settings">
          {benchlingApp.appDefinitionId && (
            <div className={css(styles.benchlingAppMetadataField)}>
              <InputWrapper
                id={_.uniqueId('benchlingAppDetail')}
                label="ID"
                labelStyle={FieldLabel.STYLES.LABEL}
              >
                <EditableTextField
                  isDisabled={true}
                  requestChange={() => {}}
                  type={TextFieldType.TEXT}
                  value={benchlingApp.appDefinitionId}
                />
              </InputWrapper>
            </div>
          )}
          <div className={css(styles.benchlingAppMetadataField)}>
            <InputWrapper
              id={_.uniqueId('benchlingAppDetail')}
              label={benchlingApp.appDefinitionId ? 'Installation ID' : 'ID'}
              labelStyle={FieldLabel.STYLES.LABEL}
            >
              <EditableTextField
                isDisabled={true}
                requestChange={() => {}}
                type={TextFieldType.TEXT}
                value={benchlingAppId}
              />
            </InputWrapper>
          </div>
          {benchlingApp.versionNumber && (
            <div className={css(styles.benchlingAppMetadataField)}>
              <InputWrapper
                id={_.uniqueId('benchlingAppDetail')}
                label="Version"
                labelStyle={FieldLabel.STYLES.LABEL}
              >
                <EditableTextField
                  isDisabled={true}
                  requestChange={() => {}}
                  type={TextFieldType.TEXT}
                  value={benchlingApp.versionNumber}
                />
              </InputWrapper>
            </div>
          )}
          <div className={css(styles.benchlingAppMetadataField)}>
            <InputWrapper
              id={_.uniqueId('benchlingAppDetail')}
              label="Name"
              labelStyle={FieldLabel.STYLES.LABEL}
            >
              <EditableTextField
                disableEditsWithTooltip={getVersionLockedTooltip()}
                requestChange={(newName: string) => {
                  handleSubmitDefinitionVersion({...currentUpdateShape, name: newName});
                }}
                type={TextFieldType.TEXT}
                value={name}
                dataTestAttrs={{component: 'EditableTextField', element: 'name'}}
              />
            </InputWrapper>
          </div>
          <div className={css(styles.benchlingAppMetadataField)}>
            <InputWrapper
              id={_.uniqueId('benchlingAppDetail')}
              label="Description"
              labelStyle={FieldLabel.STYLES.LABEL}
            >
              <EditableTextField
                disableEditsWithTooltip={getVersionLockedTooltip()}
                emptyValueAllowed={true}
                requestChange={(newDescription: string) => {
                  handleSubmitDefinitionVersion({...currentUpdateShape, description: newDescription});
                }}
                type={TextFieldType.TEXTAREA}
                value={description}
                isFullWidth={true}
                dataTestAttrs={{component: 'EditableTextField', element: 'description'}}
              />
            </InputWrapper>
          </div>
          <div className={css(styles.benchlingAppMetadataField)}>
            <PublicKeySection
              value={publicKey}
              onChangePublicKey={(newPublicKey: string) => {
                handleSubmitDefinition({publicKey: newPublicKey});
              }}
            />
          </div>
          <Header3 className="u-spacing-top">Credentials</Header3>
          Apps require data access and permissions to operate. Refer to{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://docs.benchling.com/docs/getting-started-benchling-apps#permissioning-apps"
          >
            this guide
          </a>{' '}
          to grant and confirm this app's access.
          {benchlingApp.oauthCredential && !isInaccessible(benchlingApp.oauthCredential) && (
            <ClientCredentialSection
              appName={benchlingApp.name}
              clientSecrets={(benchlingApp.oauthCredential as OAuthCredential).clientSecrets}
              clientId={(benchlingApp.oauthCredential as OAuthCredential).clientId}
              handleDelete={handleDeleteClientSecret}
              handleRegenerateSecret={handleRegenerateClientSecret}
              lifecycleManagement={benchlingApp.lifecycleManagement}
              isGlobal={benchlingApp.isGlobal}
            />
          )}
          {!benchlingApp.webhookConfigurations.some((webhookConfiguration) =>
            isInaccessible(webhookConfiguration)
          ) &&
            (BenchlingConfig.ENABLE_BENCHLING_APPS_WEBHOOK_HEADERS ? (
              <WebhookSetupSection
                webhookUrl={benchlingApp.webhookUrl}
                webhookHeaders={benchlingApp.webhookHeaders}
                webhookSetupIsAccessible={true}
                onUpdateWebhookSetup={async (
                  newWebhookUrl: string | null,
                  newWebhookHeaders: Array<{key: string; value: string}>
                ) => {
                  await handleSubmitDefinition(
                    {
                      webhookUrl: newWebhookUrl,
                      webhookHeaders: newWebhookHeaders,
                    },
                    false
                  );
                }}
              />
            ) : (
              <WebhookConfigurationSection
                webhookConfigurations={
                  benchlingApp.webhookConfigurations as Array<WebhookConfigurationSectionWebhookConfiguration>
                }
                onUpdateWebhookURL={(newWebhookURL: string) => {
                  handleSubmitDefinition({webhookUrl: newWebhookURL});
                }}
                hasFeatures={benchlingApp.features.length > 0}
                hasMessageSubscriptions={(benchlingApp.eventSubscription?.eventTypes.length ?? 0) > 0}
                lifecycleManagement={benchlingApp.lifecycleManagement}
              />
            ))}
        </Tab>
      </TabContainer>
    </SettingsPageContainer>
  );
}

const styles = StyleSheet.create({
  benchlingAppMetadataField: {
    marginBottom: 15,
  },
  maxWidth: {
    maxWidth: '70em',
  },
});
